import { useMutation, useQuery } from "@apollo/client/react";
import { useIntl, type IntlShape } from "react-intl";
import {
  CreateExperienceDocument,
  DeleteExperienceDocument,
  MyselfExpertiseDataDocument,
  UpdateExperienceDocument,
  type MyselfExpertiseDataQuery,
} from "@/graphql/generated/operations";
import { audienceRelations, type Audience } from "@/lib/audience";
import { toDateInstant } from "@/lib/date-instant";

export interface ExperienceFormInput {
  jobTitle: string;
  employer: string;
  department: string;
  location: string;
  startDate: string;
  endDate: string;
  // Optional fields surfaced for ProfilePage parity; left undefined on add()
  // means "don't touch" - the field isn't included in the payload.
  country?: string;
  industry?: string;
  activities?: readonly string[];
  sharedWith?: ReadonlyArray<Audience | string>;
}

export type ExperienceItem = NonNullable<
  NonNullable<MyselfExpertiseDataQuery["myself"]>["experiences"]
>[number];

export interface UseExperiencesResult {
  items: readonly ExperienceItem[];
  add: (input: ExperienceFormInput) => Promise<void>;
  update: (id: string, input: ExperienceFormInput) => Promise<void>;
  remove: (id: string) => Promise<void>;
  busy: boolean;
  error: Error | null;
}

function toDataInput(input: ExperienceFormInput, intl: IntlShape) {
  const startDate = toDateInstant(input.startDate);
  if (!startDate) {
    throw new Error(
      intl.formatMessage({ id: "profile.page.error.startDateRequired", defaultMessage: "Start date is required." }),
    );
  }
  if (!input.jobTitle.trim()) {
    throw new Error(
      intl.formatMessage({ id: "profile.page.error.jobTitleRequired", defaultMessage: "Job title is required." }),
    );
  }
  return {
    jobTitle: input.jobTitle,
    employer: input.employer || null,
    department: input.department || null,
    location: input.location || null,
    startDate,
    endDate: toDateInstant(input.endDate),
    ...(input.country !== undefined
      ? { country: input.country ? { id: input.country } : null }
      : {}),
    ...(input.industry !== undefined
      ? { industry: input.industry ? { id: input.industry } : null }
      : {}),
    ...(input.activities !== undefined
      ? { activities: [...input.activities] }
      : {}),
    ...(input.sharedWith !== undefined
      ? { sharedWith: audienceRelations(input.sharedWith) }
      : {}),
  };
}

export function useExperiences(): UseExperiencesResult {
  const intl = useIntl();
  const query = useQuery(MyselfExpertiseDataDocument);
  const personId = query.data?.myself?.id;

  const [createMut, createState] = useMutation(CreateExperienceDocument, {
    refetchQueries: [{ query: MyselfExpertiseDataDocument }],
  });
  const [updateMut, updateState] = useMutation(UpdateExperienceDocument, {
    refetchQueries: [{ query: MyselfExpertiseDataDocument }],
  });
  const [deleteMut, deleteState] = useMutation(DeleteExperienceDocument, {
    refetchQueries: [{ query: MyselfExpertiseDataDocument }],
  });

  async function add(input: ExperienceFormInput) {
    if (!personId) throw new Error("useExperiences: no personId yet");
    await createMut({
      variables: {
        input: { data: { ...toDataInput(input, intl), person: { id: personId } } },
      },
    });
  }

  async function update(id: string, input: ExperienceFormInput) {
    await updateMut({ variables: { id, input: { data: toDataInput(input, intl) } } });
  }

  async function remove(id: string) {
    await deleteMut({ variables: { input: { id } } });
  }

  return {
    items: query.data?.myself?.experiences ?? [],
    add,
    update,
    remove,
    busy: createState.loading || updateState.loading || deleteState.loading,
    error:
      (createState.error as Error | undefined) ??
      (updateState.error as Error | undefined) ??
      (deleteState.error as Error | undefined) ??
      null,
  };
}
